/**
 * HistoryMenu Component
 * Dropdown menu showing recent invoice history (last 5 items) with localStorage persistence
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useHistoryStore } from '@/stores/historyStore';
import { useInvoiceStore } from '@/stores/invoiceStore';
import { formatDate } from '@/utils/date';
import type { Invoice } from '@shared/schemas/invoice.schema';

// Helper function to calculate invoice total
function calculateInvoiceTotal(invoice: Invoice): number {
  const subtotal = invoice.subtotal;
  const discountAmount = (subtotal * invoice.discountRate) / 100;
  const shipping = invoice.shipping || 0;
  const taxAmount = ((subtotal - discountAmount) * invoice.taxRate) / 100;
  return subtotal - discountAmount + shipping + taxAmount;
}

export function HistoryMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { history, isOpen, setOpen, removeFromHistory, clearHistory, loadFromHistory } =
    useHistoryStore();
  const { setCurrentInvoice } = useInvoiceStore();
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Show only last 5 items in dropdown
  const recentHistory = history.slice(0, 5);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setOpen]);

  const handleLoadInvoice = (invoice: Invoice) => {
    setCurrentInvoice(invoice);
    loadFromHistory();
  };

  const handleRemoveInvoice = (e: React.MouseEvent, invoiceId: string) => {
    e.stopPropagation();
    removeFromHistory(invoiceId);
  };

  const handleClearHistory = () => {
    if (confirm(t('history.clearConfirm'))) {
      clearHistory();
    }
  };

  const handleDropdownRender = (element: HTMLDivElement | null) => {
    if (element && isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const parentRect = buttonRef.current.parentElement?.getBoundingClientRect();

      if (parentRect) {
        // Calculate position to align dropdown's right edge with button's right edge
        const buttonRightFromParent = buttonRect.right - parentRect.left;
        const rightPosition = parentRect.width - buttonRightFromParent;

        element.style.right = `${rightPosition}px`;
        element.style.left = 'auto';
      }
    }
  };

  return (
    <div className="relative">
      {/* History Button */}
      <button
        ref={buttonRef}
        onClick={() => setOpen(!isOpen)}
        className="px-2 sm:px-3 py-1.5 text-sm rounded-lg transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 flex items-center gap-2 min-w-0 sm:min-w-[120px] justify-between"
        aria-label={t('history.ariaLabel')}
      >
        <span className="flex items-center gap-2 min-w-0">
          <div className="relative flex-shrink-0">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {history.length > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-primary-600 text-white text-[10px] rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5">
                {history.length > 9 ? '9+' : history.length}
              </span>
            )}
          </div>
          <span className="hidden sm:inline">{t('history.label')}</span>
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          ref={(el) => {
            (menuRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
            handleDropdownRender(el);
          }}
          className="fixed left-4 right-4 top-auto mt-2 sm:absolute sm:mt-2 sm:w-96 max-h-[80vh] overflow-hidden bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 z-50 flex flex-col"
          style={{ minWidth: '384px', right: '0', left: 'auto' }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('history.title')}
            </h3>
            {history.length > 0 && (
              <button
                onClick={handleClearHistory}
                className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
              >
                {t('history.clearAll')}
              </button>
            )}
          </div>

          {/* History List */}
          <div className="flex-1 overflow-y-auto">
            {history.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                <svg
                  className="w-12 h-12 mx-auto mb-3 text-gray-400 dark:text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <p>{t('history.empty')}</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {recentHistory.map((item) => (
                  <div
                    key={item.invoice.id}
                    onClick={() => handleLoadInvoice(item.invoice)}
                    className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors group"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                            {item.invoice.invoiceNumber || formatDate(typeof item.invoice.invoiceDate === 'string' ? item.invoice.invoiceDate : new Date(item.invoice.invoiceDate).toISOString()) || t('history.unnumbered')}
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {item.invoice.currency}
                            {calculateInvoiceTotal(item.invoice).toFixed(2)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                          {item.invoice.fromCompany} → {item.invoice.toCompany}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-500">
                          {formatDate(item.savedAt)}
                        </div>
                      </div>
                      <button
                        onClick={(e) => handleRemoveInvoice(e, item.invoice.id!)}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-all"
                        aria-label={t('history.remove')}
                      >
                        <svg
                          className="w-4 h-4 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {history.length > 0 && (
            <>
              {history.length > 5 && (
                <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 text-center">
                  <button
                    onClick={() => navigate({ to: '/history' })}
                    className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium transition-colors"
                  >
                    {t('history.viewAll')}
                  </button>
                </div>
              )}
              <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 text-center">
                {t('history.itemCount', { count: history.length })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
